import { merchantUsersApi } from 'api/merchantUsers'
import { rolesApi } from 'api/roles'
import { isAdminRole } from 'constants/roles'
import useFlag from 'hooks/useFlag'
import { useSelector } from 'react-redux'
import { useAbility } from 'utilities/ability'

/**
 * Returns true if the current user has the standard admin preset role assigned
 * via RBAC, plus a loading indicator for callers that need to distinguish
 * "not admin" from "still resolving." Cross-references rbac_role_ids against
 * the roles list, matching by name + preset (not by ID) to survive table reseeds.
 */
export const useIsRbacAdminWithLoading = (): {
  isRbacAdmin: boolean
  isLoading: boolean
} => {
  const rbacEnabled = useFlag('role-based-access-control', false)
  const ability = useAbility()
  // `canViewUsers=false` here means "genuinely lacks MerchantUsers access," not
  // "still loading." Permissions load atomically with the ORM merchant_user record
  // (single /me payload), AbilityProvider (an ancestor of the routes) rebuilds the
  // ability from that record before any route reads it, and ProtectedRoute gates on
  // !merchantUser upstream — so this hook's result is only consumed for routing once
  // the ability is resolved. The one render where the ability lacks MerchantUsers is
  // when merchantUser is null, which is exactly when ProtectedRoute shows its spinner.
  const canViewUsers = ability?.can('access', 'MerchantUsers')
  const shouldFetch = rbacEnabled && canViewUsers
  const currentUserId = useSelector(
    (state: { auth: { current_merchant_user_id: string | null } }) =>
      state.auth.current_merchant_user_id
  )

  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesError,
  } = rolesApi.useGetRolesQuery(undefined, { skip: !shouldFetch })
  const {
    data: userData,
    isLoading: userLoading,
    isError: userError,
  } = merchantUsersApi.useGetMerchantUserQuery(String(currentUserId), {
    skip: !currentUserId || !shouldFetch,
  })

  if (!shouldFetch) {
    return { isRbacAdmin: false, isLoading: false }
  }

  // The merchant user query is skipped when currentUserId is null. A skipped
  // query is "cannot determine," not "in flight" — RTK reports isLoading=false
  // with data=undefined, so the !userData guard below would otherwise misread
  // it as loading. Return not-loading here so the distinction is explicit.
  if (!currentUserId) {
    return { isRbacAdmin: false, isLoading: false }
  }

  // A terminal fetch error (network/5xx/timeout) leaves data=undefined with
  // isLoading=false and no automatic retry. Treat it as "not admin, done"
  // rather than letting the !data guard below report isLoading=true forever —
  // on an adminOnly route that would hang the spinner. Redirecting matches the
  // pre-loading-state behavior and lets the user retry from home.
  if (rolesError || userError) {
    return { isRbacAdmin: false, isLoading: false }
  }

  if (rolesLoading || userLoading || !rolesData || !userData) {
    return { isRbacAdmin: false, isLoading: true }
  }

  const adminRole = rolesData.roles.find(isAdminRole)
  const rbacRoleIds = userData.merchant_user?.rbac_role_ids ?? []

  return {
    isRbacAdmin: !!adminRole && rbacRoleIds.includes(adminRole.id),
    isLoading: false,
  }
}

export const useIsRbacAdmin = (): boolean =>
  useIsRbacAdminWithLoading().isRbacAdmin
